import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = {
  id: "", name: "", price: "", schedule: "", days: "",
  notes: "", category: "standard", order: 0, active: true,
};

export default function AdminTariffs() {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(blank);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tariffs/all");
      setTariffs(data);
    } catch {
      toast.error("Error carregant tarifes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(blank); setDialogOpen(true); };
  const openEdit = (tf) => { setEditing({ ...tf }); setDialogOpen(true); };

  const onSave = async (e) => {
    e.preventDefault();
    if (!editing.name || editing.price === "" || !editing.schedule || !editing.days) {
      toast.error("Nom, preu, horari i dies són obligatoris.");
      return;
    }
    const payload = {
      name: editing.name,
      price: parseFloat(editing.price),
      schedule: editing.schedule,
      days: editing.days,
      notes: editing.notes || "",
      category: editing.category,
      order: parseInt(editing.order || 0, 10),
      active: editing.active,
    };
    try {
      if (editing.id) {
        await api.put(`/tariffs/${editing.id}`, payload);
        toast.success("Tarifa actualitzada.");
      } else {
        await api.post("/tariffs", payload);
        toast.success("Tarifa creada.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardant.");
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tariffs/${confirmDelete.id}`);
      toast.success("Tarifa eliminada.");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Error eliminant.");
    }
  };

  return (
    <div className="bg-white border border-[#eae0df] rounded-2xl overflow-hidden" data-testid="admin-tariffs-table">
      <div className="px-6 py-5 flex items-center justify-between border-b border-[#eae0df]">
        <div>
          <p className="uppercase tracking-[0.22em] text-[10px] text-[#3e664c] mb-1">Tarifes</p>
          <h2 className="font-display text-2xl">Gestió de tarifes</h2>
        </div>
        <Button onClick={openCreate} data-testid="admin-create-tariff"
          className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-white gap-2 px-5 py-4 text-xs uppercase tracking-[0.18em]">
          <Plus size={14} /> Nova tarifa
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#fdfcf9] text-[#5C544F]">
            <tr>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Categoria</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Nom</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Preu</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Horari</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Dies</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px]">Estat</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-[#5C544F]">…</td></tr>
            ) : tariffs.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-[#5C544F]">No hi ha tarifes.</td></tr>
            ) : tariffs.map((tf) => (
              <tr key={tf.id} className="border-t border-[#eae0df]" data-testid={`admin-tariff-row-${tf.id}`}>
                <td className="px-5 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] ${tf.category === "special" ? "bg-[#5daa92]/15 text-[#3e664c]" : "bg-[#3e664c]/10 text-[#3e664c]"}`}>
                    {tf.category === "special" ? "Especial" : "Estàndard"}
                  </span>
                </td>
                <td className="px-5 py-3 font-medium">{tf.name}</td>
                <td className="px-5 py-3 text-[#3e664c] font-medium">{tf.price.toFixed(0)} €</td>
                <td className="px-5 py-3 text-[#5C544F]">{tf.schedule}</td>
                <td className="px-5 py-3 text-[#5C544F]">{tf.days}</td>
                <td className="px-5 py-3">{tf.active ? "Actiu" : "Inactiu"}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(tf)} data-testid={`admin-edit-tariff-${tf.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(tf)} data-testid={`admin-delete-tariff-${tf.id}`}><Trash2 size={14} className="text-[#450905]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="admin-tariff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing.id ? "Editar tarifa" : "Nova tarifa"}</DialogTitle>
            <DialogDescription className="text-[#5C544F]">Aquesta tarifa apareixerà a la web pública.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Nom</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tariff-input-name" required className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Categoria</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="tariff-input-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Estàndard</SelectItem>
                    <SelectItem value="special">Especial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Preu (€)</Label>
                <Input type="number" step="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="tariff-input-price" required className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Horari</Label>
                <Input value={editing.schedule} onChange={(e) => setEditing({ ...editing, schedule: e.target.value })} placeholder="10:00h - 17:00h" data-testid="tariff-input-schedule" required className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Dies</Label>
                <Input value={editing.days} onChange={(e) => setEditing({ ...editing, days: e.target.value })} placeholder="Dilluns a Dijous" data-testid="tariff-input-days" required className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Notes</Label>
                <Input value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="+ IVA · Neteja no inclosa" data-testid="tariff-input-notes" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Ordre</Label>
                <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="tariff-input-order" className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="tariff-dialog-cancel">Cancel·lar</Button>
              <Button type="submit" className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-white px-6" data-testid="tariff-dialog-save">
                {editing.id ? "Guardar canvis" : "Crear tarifa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Eliminar tarifa?</AlertDialogTitle>
            <AlertDialogDescription>S&apos;eliminarà {confirmDelete?.name}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-[#450905] hover:bg-[#2c0603]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
